/**
 * Use this to distinguish a local dev environment pointing to dev/staging/production backend
 * from an actual deployment to dev.mach.exchange or staging.mach.exchange.
 *
 * This is useful if we want to locally test features that are not enabled on
 * dev.mach.exchange or staging.mach.exchange.
 *
 * By default we enable every feature locally, modify if needed.
 */

// Define all possible environment values as an enum
export enum Environment {
    LOCAL_DEV = 'LOCAL_DEV',
    LOCAL_STAGING = 'LOCAL_STAGING',
    LOCAL_PRODUCTION = 'LOCAL_PRODUCTION',
    LOCAL_TESTNET = 'LOCAL_TESTNET',
    LOCAL_TESTNET_DEV = 'LOCAL_TESTNET_DEV',
    DEV = 'DEV',
    STAGING = 'STAGING',
    PRODUCTION = 'PRODUCTION',
    TESTNET = 'TESTNET',
    TESTNET_DEV = 'TESTNET_DEV',
}

// Create an array of all environment values for validation purposes
export const ENVIRONMENT_KEYS = Object.values(Environment)

// Use type-safe environment array
const LOCAL_ANY: Environment[] = [
    Environment.LOCAL_DEV,
    Environment.LOCAL_STAGING,
    Environment.LOCAL_PRODUCTION,
    Environment.LOCAL_TESTNET,
    Environment.LOCAL_TESTNET_DEV,
]

const LOCAL_NON_TESTNET: Environment[] = [
    Environment.LOCAL_DEV,
    Environment.LOCAL_STAGING,
    Environment.LOCAL_PRODUCTION,
]

export const FEATURE_GATE_KEYS = [
    'ENABLE_TESTNETS',
    'USE_TESTNETS_ONLY',
    'TRANSACTION_CAP',
    'ONRAMP',
    'OFFRAMP',
    'SHOW_TRON_CHAIN',
    'ENABLE_EXPERIMENTAL_CHAINS',
    'ENABLE_MANTLE_CHAIN',
    'ENABLE_CELO_CHAIN',
    'ENABLE_FEATHER',
    'V2_MIGRATION',
    'MILES_REQUIREMENT',
    'ABSTRACT_CONNECTOR',
    'WRAPPED_ASSETS_MESSAGE',
    'ONLY_SOL_USDC',
    'SAND_DOLLARS',
] as const

export type FeatureGateKey = (typeof FEATURE_GATE_KEYS)[number]

export const FEATURE_GATES: Readonly<Record<FeatureGateKey, Environment[]>> = {
    // Testnet related feature gates
    ENABLE_TESTNETS: [
        Environment.LOCAL_TESTNET,
        Environment.LOCAL_TESTNET_DEV,
        Environment.TESTNET,
        Environment.TESTNET_DEV,
    ],
    USE_TESTNETS_ONLY: [
        Environment.LOCAL_TESTNET,
        Environment.LOCAL_TESTNET_DEV,
        Environment.TESTNET,
        Environment.TESTNET_DEV,
    ],
    TRANSACTION_CAP: [
        Environment.LOCAL_TESTNET,
        Environment.LOCAL_TESTNET_DEV,
        Environment.TESTNET,
        Environment.TESTNET_DEV,
    ],

    // Enable wrapped assets message in all environments except production
    WRAPPED_ASSETS_MESSAGE: LOCAL_ANY.concat([Environment.DEV, Environment.STAGING]),

    // Abstract connector only enabled in non-testnet environments
    ABSTRACT_CONNECTOR: LOCAL_NON_TESTNET.concat([Environment.DEV, Environment.STAGING, Environment.PRODUCTION]),

    MILES_REQUIREMENT: [Environment.LOCAL_TESTNET, Environment.TESTNET, Environment.TESTNET_DEV], // Gate MON/WMON purchases behind miles requirement on testnet

    // Fiat onramp and offramp related feature gates
    ONRAMP: LOCAL_ANY.concat([Environment.DEV]),
    OFFRAMP: LOCAL_ANY.concat([Environment.DEV]),

    // General feature gates
    SHOW_TRON_CHAIN: LOCAL_ANY.concat([Environment.DEV, Environment.STAGING]), // gate all tron token assets on the tron network until backend is working
    ENABLE_EXPERIMENTAL_CHAINS: LOCAL_ANY.concat([Environment.DEV, Environment.STAGING]), // Enable in non-prod environments
    ENABLE_MANTLE_CHAIN: LOCAL_ANY.concat([Environment.DEV, Environment.STAGING]), // Enable in non-prod environments
    ENABLE_CELO_CHAIN: LOCAL_ANY.concat([Environment.DEV, Environment.STAGING]),
    ENABLE_FEATHER: LOCAL_NON_TESTNET.concat([Environment.DEV]), // Use Feather Orders

    // Do not enable v2 migration for LOCAL_TESTNET and LOCAL_TESTNET_DEV
    V2_MIGRATION: LOCAL_NON_TESTNET.concat([Environment.DEV, Environment.STAGING, Environment.PRODUCTION]), // Enable v2 migration only for non-testnet environments

    // Feature to only show USDC token on Solana chain
    ONLY_SOL_USDC: [], // Only enabled in local environments for now

    // Sand Dollars feature - enabled in DEV and LOCAL_DEV environments
    SAND_DOLLARS: [Environment.LOCAL_DEV, Environment.DEV],
}
